import { AlertTriangle } from "lucide-react";
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from "react-router";
import { Button } from "@altek/ui/components/ui/Button";
import { ApiError, messageFor } from "@altek/ui/lib/problem";
import { useScreenTitle } from "@/app/layouts/document-title";
import { supportCodeOf } from "@/lib/api/support-code";
import { env } from "@/lib/env";

/**
 * What renders when a route throws.
 *
 * Scoped to the route, not the application: the shell around it — sidebar, header, scope
 * selector — is still working, so the user can navigate away instead of reloading. A
 * whole-page error screen for one broken screen throws away a working session.
 *
 * The request id is shown when there is one. It is the only thing that lets somebody find
 * this exact failure in the server's logs, and asking a user to "describe what happened" when
 * the system already knows the answer wastes everybody's time.
 *
 * KABUĞUN İÇİNDE (9 Ekim 2026): `router.tsx` bu sayfayı ekranların yolsuz ara rotasına da
 * bağlıyor, yani düşen bir ekranda ray ve üst çubuk yerinde kalıyor. Sekme başlığı da hatayı
 * söylüyor — yoksa düşen ekranın adını taşımaya devam ediyordu. Geçmişi olmayan bir sekmede
 * (bağlantıyla doğrudan açılmış) «Geri dön» hiçbir yere gitmiyordu; orada düğme başlangıca götürür.
 *
 * KABUĞUN DIŞINDA DA (9 Ekim 2026, 5. tur): giriş, davet ve parola sıfırlama rotalarının da hata
 * sınırı bu sayfa — yoksa yeni bir dağıtımdan sonra yüklenemeyen bir parça React Router'ın kendi
 * İngilizce «Unexpected Application Error!» ekranını açıyordu.
 *
 * «DESTEK KODU», «İstek no» değil, ve YALNIZ SUNUCU ARIZASINDA (5xx) — ürünün geri kalanıyla aynı
 * ad ve aynı kural (`support-code.ts`). Kod varken ne yapılacağı da yazıyor: sistem yöneticisine.
 */
export default function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();

  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  const apiError = error instanceof ApiError ? error : null;
  const supportCode = supportCodeOf(apiError);
  /** React Router ilk girişe `"default"` anahtarını veriyor: geri gidilecek bir sayfa yok. */
  const canGoBack = location.key !== "default";

  useScreenTitle(isNotFound ? "Sayfa bulunamadı" : "Bu sayfa açılamadı");

  return (
    <div className="grid min-h-[60vh] place-items-center p-6">
      <div className="w-full max-w-md text-center">
        <AlertTriangle size={28} strokeWidth={1.6} className="mx-auto text-danger" aria-hidden="true" />
        <h1 className="mt-3 text-md font-semibold text-fg">
          {isNotFound ? "Sayfa bulunamadı" : "Bu sayfa açılamadı"}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {apiError
            ? messageFor(apiError)
            : "Beklenmeyen bir hata oluştu. Sayfayı yeniden deneyebilirsiniz."}
        </p>

        {supportCode ? (
          <>
            <p className="mt-3 text-sm text-fg-muted">
              Sorun sürerse bu kodu sistem yöneticinize iletin.
            </p>
            <p className="mt-1 font-mono text-2xs text-fg-subtle">Destek kodu: {supportCode}</p>
          </>
        ) : null}

        {env.isDev && error instanceof Error ? (
          <pre className="mt-3 max-h-40 overflow-auto rounded-sm bg-surface-2 p-3 text-left font-mono text-2xs text-fg-muted">
            {error.stack ?? error.message}
          </pre>
        ) : null}

        <div className="mt-4 flex justify-center gap-2">
          <Button
            variant="secondary"
            className="max-md:h-11"
            onClick={() => void (canGoBack ? navigate(-1) : navigate("/"))}
          >
            {canGoBack ? "Geri dön" : "Başlangıca dön"}
          </Button>
          <Button className="max-md:h-11" onClick={() => window.location.reload()}>
            Yeniden dene
          </Button>
        </div>
      </div>
    </div>
  );
}
